import propTypes from 'prop-types'
import React from 'react'
import map from 'lodash/map'
import find from 'lodash/find'
import isEmpty from 'lodash/isEmpty'
import filter from 'lodash/filter'
import { Link } from 'react-router-dom'
import Moment from 'react-moment'
import {
  FaDownload,
  FaFacebook,
  FaLink,
  FaLinkedin,
  FaPinterest,
  FaRegComment,
  FaRegEnvelope,
  FaRegHeart,
  FaRegShareSquare,
  FaTelegram,
  FaTimes,
  FaTwitter,
  FaWhatsapp,
} from 'react-icons/fa'
import { useClient, useMutation } from 'jsonapi-react'
import moment from 'moment'
import { useCopyToClipboard } from 'react-use'
import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  PinterestShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from 'react-share'

import { API_BASE_URL } from 'App'
import Dropdown from 'components/Dropdown'
import { Placeholder } from './Placeholder'
import Avatar from 'components/Avatar'
import Modal from 'components/Modal'
import Select from 'components/Select'
import Button from 'components/Button'
import List from 'components/List'
import Loader from 'components/Loader'


export default function BaseComposition(props) {
  const [seeMore, setSeeMore] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const [showDownloadModal, setShowDownloadModal] = React.useState(false)
  const [modalShare, setModalShare] = React.useState(false)
  const [modalDelete, setModalDelete] = React.useState(false)
  const interval = React.useRef(null)
  const [isCopied, copyToClipboard] = useCopyToClipboard()

  const client = useClient()
  const [updateComposition] = useMutation([
    'compositions',
    props.id,
  ])
  const [addCompositionDownload] = useMutation([
    'compositions',
    props.id,
    'downloads',
  ])

  const deleteComposition = async (id) => {
    setDeleting(true)
    await client.delete(['compositions', id])
    props?.onDelete()
    setDeleting(false)
  }

  const actionButtons = []

  const fetchItem = async id => {
    const item = await client.fetch([
      'compositions',
      id,
      {
        include: 'created_by,created_by.profile_pic,exported_file,downloads.file',
        'fields[Composition]': 'title,description,permissions,privacy,created_by,created_at,exported_file,status,downloads',
        'fields[User]': 'full_name,profile_pic',
        'fields[File]': 'title,type,file,thumbnail,medium',
      },
    ])

    props?.onReload(item.data)

    return item
  }

  React.useEffect(() => {

    interval.current = setInterval(
      async () => {
        if (
          (
            props.status !== 'processed' &&
            props.status !== 'error'
          ) ||
          find(
            props.downloads,
            download => (
              download.status !== 'processed' ||
              download.status !== 'error'
            )
          )
        ) {

          const item = await fetchItem(props.id)

          if (
            (
              item.data.status === 'processed' ||
              item.data.status === 'error'
            ) && ! find(
              props.downloads,
              download => (
                download.status !== 'processed' &&
                download.status !== 'error'
              )
            )
          ) {
            clearInterval(interval.current)
          }
        } else {
          clearInterval(interval.current)
        }
      },
      10000,
    )

    return () => {
      if (interval.current)
        clearInterval(interval.current)
    }
  }, []) // eslint-disable-line


  if (props.permissions?.delete) {
    actionButtons.push(
      {
        'label' : 'Delete',
        'onClick' : (e) => {
          e.preventDefault()
          e.stopPropagation()
          setModalDelete(true)
        }
      }
    )
  }

  if (props.permissions?.edit) {
    const newPrivacy = props.privacy === 'public' ? 'private' : 'public'
    actionButtons.push(
      {
        'label' : 'Make ' + newPrivacy,
        'onClick' : async (e) => {
          e.preventDefault()
          e.stopPropagation()
          await updateComposition({
            privacy: newPrivacy,
          })

          props?.onUpdate({
            ...props,
            privacy: newPrivacy,
          })
        }
      }
    )
  }

  if (props.permissions?.download && props.status === 'processed') {
    actionButtons.push(
      {
        'label' : 'Download',
        'onClick' : (e) => {
          e.preventDefault()
          e.stopPropagation()
          setShowDownloadModal(true)
        }
      }
    )
  }

  const onSubmitDownloadForm = async e => {
    e.preventDefault()

    const size = e.currentTarget.elements[0].value

    await addCompositionDownload({
      size,
    })
    const item = await fetchItem(props.id)

    props?.onUpdate(item.data)
  }

  const options = filter(
    [
      {
        'label': 'Desktop (1280 x 720)',
        'value': 'desktop',
      },
      {
        'label': 'Square (1280 x 1280)',
        'value': 'square',
      },
      {
        'label': 'Story (720 x 1280)',
        'value': 'story',
      },
    ],
    size => !find(props.downloads, _download => size.value === _download.size)
  )

  return (
    <div className='composition shadow-md mb-5 bg-white w-full relative'>
      <Modal active={modalDelete} onClose={() => setModalDelete(false)}>
        <Modal.Content className='prose-sm sm:prose-lg'>
          <h2>Are you sure to remove the composition?</h2>
          This action is not reversible.<br />
          <Button
            onClick={() => deleteComposition(props.id)}
            className='inverted'
            theme='danger'
          >Delete</Button>
          <Button
            onClick={() => setModalDelete(false)}
            className='inverted'
            theme=''
          >Cancel</Button>
        </Modal.Content>
      </Modal>
      <Modal active={modalShare} onClose={() => setModalShare(false)}>
        <Modal.Content>
          <div className='prose-sm sm:prose-lg'>
            <h2>Share</h2>
            <div className='flex space-x-3'>
              <EmailShareButton
                url={`${API_BASE_URL}compositions/${props.id}/share/`}
                subject={`See my composition ${props.title}`}
                body={props.description}
                className='flex space-x-3 items-center hover:opacity-80 btn'
                resetButtonStyle={false}
              >
                <FaRegEnvelope className='w-8 h-8'/>
              </EmailShareButton>
              <FacebookShareButton
                url={`${API_BASE_URL}compositions/${props.id}/share/`}
                quote={`See my composition ${props.title}`}
                hashtag='#improvisacomposition #improvisa $lifeinmotion'
                className='flex space-x-3 items-center hover:opacity-80 btn'
                style={{ color: '#3b5998'}}
                resetButtonStyle={false}
              >
                <FaFacebook className='w-8 h-8'/>
              </FacebookShareButton>
              <LinkedinShareButton
                url={`${API_BASE_URL}compositions/${props.id}/share/`}
                title={`See my composition ${props.title}`}
                summary={props.description}
                source='Improvisa'
                style={{ color: '#0e76a8'}}
                className='flex space-x-3 items-center hover:opacity-80 btn'
                resetButtonStyle={false}
              >
                <FaLinkedin className='w-8 h-8'/>
              </LinkedinShareButton>
              <PinterestShareButton
                url={`${API_BASE_URL}compositions/${props.id}/share/`}
                media={props?.exportedFile?.file}
                description={props.description}
                style={{ color: '#c8232c'}}
                className='flex space-x-3 items-center hover:opacity-80 btn'
                resetButtonStyle={false}
              >
                <FaPinterest className='w-8 h-8'/>
              </PinterestShareButton>
              <TelegramShareButton
                url={`${API_BASE_URL}compositions/${props.id}/share/`}
                title={`See my composition ${props.title}`}
                className='flex space-x-3 items-center hover:opacity-80 btn'
                style={{ color: '#0088CC'}}
                resetButtonStyle={false}
              >
                <FaTelegram className='w-8 h-8'/>
              </TelegramShareButton>
              <TwitterShareButton
                url={`${API_BASE_URL}compositions/${props.id}/share/`}
                title={`See my composition ${props.title}`}
                via='Improvisa'
                style={{ color: '#00acee'}}
                hashtags={['improvisacomposition', 'improvisa', 'life in motion']}
                className='flex space-x-3 items-center hover:opacity-80 btn'
                resetButtonStyle={false}
              >
                <FaTwitter className='w-8 h-8'/>
              </TwitterShareButton>
              <WhatsappShareButton
                url={`${API_BASE_URL}compositions/${props.id}/share/`}
                title={`See my composition ${props.title}`}
                className='flex space-x-3 items-center hover:opacity-80 btn'
                resetButtonStyle={false}
                style={{ color: '#4FCE5D'}}
                separator='\n'
              >
                <FaWhatsapp className='w-8 h-8'/>
              </WhatsappShareButton>
            </div>
            <div className='mt-10'>
              <a
                href={`${API_BASE_URL}compositions/${props.id}/share/`}
                className='btn default flex flex-row space-x-3 items-center'
                onClick={e => {
                  e.preventDefault()
                  copyToClipboard(`${API_BASE_URL}compositions/${props.id}/share/`)
                }}
              >
                <FaLink className='w-6 h-6' />
                <span className='truncate flex-grow-0'>{`${API_BASE_URL}compositions/${props.id}/share/`}</span>
                {
                  isCopied.error ?
                  <span className='text-danger-600 flex-grow'>{'(Error)'}</span> :
                    isCopied.value ?
                    <span className='text-secondary-600 flex-grow'>{'(Copied)'}</span> :
                    <span className='flex-grow whitespace-nowrap'>{'(Click to copy link to share)'}</span>
                }
              </a>
            </div>
          </div>
        </Modal.Content>
      </Modal>
      <Modal
        active={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        className='p-5 sm:p-10'
      >
        <Modal.Content>
          <div className='prose-sm sm:prose-lg'>
            <h2>
              Download Composition
            </h2>
            <p>
              The download process may take a few minutes.
              Select the size with which you want to download the composition, and then wait until the composition is finished.
            </p>
            {
              ! isEmpty(options) &&
              <form onSubmit={onSubmitDownloadForm}>
                <p>
                  <Select
                    selectProps={{
                      defaultValue: 'desktop',
                    }}
                    options={options}
                  />
                </p>
                <Button type='submit' className='inverted'>Generate File</Button>
              </form>
            }
            <List>
              {
                map(
                  props.downloads,
                  download => (
                    <List.Item key={`list_download_${download.id}`}>
                      <div className='flex-grow'>
                        {moment(download.createdAt).format('lll')}
                      </div>
                      <div className='flex-grow'>
                        {download.size}
                      </div>
                        {
                          download.status === 'processed' ?
                          <a
                            className='btn primary p-5'
                            href={download.file.file}
                            target='blank'
                          >
                            <FaDownload />
                          </a> : (
                            download.status === 'error' ?
                            <div className='p-5 text-danger-600'>
                              Error
                            </div> :
                            <div className='p-5'>
                              <Loader active />
                            </div>
                          )
                        }
                        <Button
                          theme='danger'
                          className='p-5'
                          disabled={ download.status !== 'processed' }
                          onClick={
                            async () => {
                              await client.delete([
                                'compositions',
                                props.id,
                                'downloads',
                                download.id,
                              ])
                              fetchItem(props.id)
                            }
                          }
                        >
                          <FaTimes />
                        </Button>
                    </List.Item>
                  ),
                )
              }
            </List>
          </div>
        </Modal.Content>
      </Modal>
      <div className='hover:border-red-600'>
        {
          props.status !== 'processed' || deleting ? (
            <Placeholder status={props.status} forceLoader={deleting} />
          ) : (
            <video
              src={props?.exportedFile?.large || props?.exportedFile?.file}
              width='100%'
              controls
              preload='metadata'
              poster={props?.exportedFile?.thumbnail}
            >
              Tu navegador no admite el elemento <code>video</code>.
            </video>
          )
        }
        <div className='p-5'>
          <div className='mb-2'>
            <div className='flex align-middle mb-2'>
              <Avatar
                link={`/users/${props.createdBy.id}/compositions`}
                user={props.createdBy}
                horizontal
                fullNameClassName='ml-2'
                size='xsmall'
              />
              <span className='text-gray-500 text-sm ml-auto self-center'>
                <Moment fromNow>{moment(props.createdAt).format()}</Moment>
              </span>
            </div>
          </div>
          <div className='text-lg font-bold'>
            {props.title}
          </div>
          {
            props.status === 'processing' &&
            <span className='text-red-600 text-lg'> (Processing)</span>
          }
          <p className=''>
            {
              props.description.length <= 150 ?
              props.description :
              <>
                {props.description.substr(0, seeMore ? props.description.length : 150)}
                {!seeMore && '...'}{' '}
                <Link className='underline' onClick={() => setSeeMore(!seeMore)}>{!seeMore ? 'more' : 'less'}</Link>
              </>
            }
          </p>
          <span
            className='text-xs px-2 font-medium bg-gray-500 bg-opacity-10 text-gray-800 rounded py-0.5'
          >
            {props.privacy}
          </span>

          <div className='flex pt-4 border-t border-disabled-100 mt-4 items-center'>
            <div className='flex flex-grow space-x-5 text-lg'>
              {/* <Button>
                <FaRegHeart />
              </Button>
              <Button>
                <FaRegComment />
              </Button> */}
              <Button onClick={() => setModalShare(true)}>
                <FaRegShareSquare />
              </Button>
            </div>
            {
              actionButtons.length > 0 &&
              <Dropdown
                itemClassName='bottom-full right-0 w-32'
                items={actionButtons}
              />
            }
          </div>
        </div>
      </div>
    </div>
  )
}

BaseComposition.propTypes = {
  date: propTypes.string,
  title: propTypes.string.isRequired,
  description: propTypes.string,
  onDelete: propTypes.func,
  onReload: propTypes.func,
  onUpdate: propTypes.func,
}

BaseComposition.defaultProps = {
  onDelete: () => {},
  onReload: () => {},
  onUpdate: () => {},
}
